// Corpos dos personagens. As três silhuetas ('padrao', 'esguio', 'robusto') saem do mesmo
// construtor (makeBody), mudando só a BodyShape.
//
// Geometria fixa (ver ../types.ts): cabeça 16×12 em y=0, tronco 16×8 em y=11, pernas
// 16×6 em y=18. Slots: skin (pele), shirt (manga e camisa), pants, shoe. 'o' = contorno.
//
// A cabeça é digitada à mão (detalhe fino: olhos, orelha). Tronco e pernas saem do Pix
// (retângulos sombreados + contorno automático), o que deixa os três corpos coerentes.

import { Pix, sprite } from '../pix.js';
import type { ArmPose, BodyDef, DirSet, LegPose, PixelSprite } from '../types.js';

const SKIN = { o: 'ink', D: 'skin1', S: 'skin2', L: 'skin3', e: 'ink', m: 'mouth', b: 'blush' };
const CLOTH = { o: 'ink', d: 'shirt1', c: 'shirt2', l: 'shirt3', S: 'skin2', T: 'skin1', p: 'pants1', P: 'pants2', Q: 'pants3', k: 'shoe1', K: 'shoe2' };

// ---------------------------------------------------------------- cabeça

const HEAD_DOWN = sprite(
  [
    '................',
    '.....oooooo.....',
    '....oLLLSSSo....',
    '...oLLSSSSSDo...',
    '...oLSSSSSSDo...',
    '..oLSSSSSSSSDo..',
    '..oSSeSSSSeSDo..',
    '..oSSeSSSSeSDo..',
    '..oSbSSSSSSbDo..',
    '...oSSSmmSSDo...',
    '....oDSSSSDo....',
    '......oDDo......',
  ],
  SKIN,
);

const HEAD_UP = sprite(
  [
    '................',
    '.....oooooo.....',
    '....oLLLSSSo....',
    '...oLLSSSSSDo...',
    '...oLSSSSSSDo...',
    '..oLSSSSSSSSDo..',
    '..oSSSSSSSSSDo..',
    '..oSSSSSSSSSDo..',
    '..oSSSSSSSSSDo..',
    '...oSSSSSSSDo...',
    '....oDSSSSDo....',
    '......oDDo......',
  ],
  SKIN,
);

const HEAD_LEFT = sprite(
  [
    '................',
    '.....oooooo.....',
    '....oLLSSSSo....',
    '...oLSSSSSSDo...',
    '...oLSSSSSSDo...',
    '..oLSSSSSSSSDo..',
    '..oSeSSSSDSSDo..',
    '..oSeSSSSDDSDo..',
    '.oSSSSSSSSSSDo..',
    '..oSmSSSSSSDo...',
    '...oDSSSSSDo....',
    '......oDDo......',
  ],
  SKIN,
);

// ---------------------------------------------------------------- tronco e pernas

/** Parâmetros de silhueta: é o que diferencia 'padrao', 'esguio' e 'robusto'. */
export interface BodyShape {
  /** Primeira e última coluna do tronco (frente/costas). */
  from: number;
  to: number;
  /** Largura de cada braço (1 ou 2). */
  arm: number;
  /** Largura de cada perna. */
  leg: number;
}

function torsoFront(shape: BodyShape, pose: ArmPose, back: boolean): PixelSprite {
  const { from, to, arm } = shape;
  const w = to - from + 1;
  const pix = new Pix(16, 8);
  pix.box(from, 0, w, 7, { fill: 'c', light: 'l', dark: 'd' });
  pix.hline(from + 1, 7, w - 2, 'P'); // cós da calça
  const leftX = from - arm;
  const rightX = to + 1;
  if (pose === 'down' || pose === 'raise') {
    pix.rect(leftX, 1, arm, 5, 'c').vline(leftX, 1, 5, 'l').rect(leftX, 6, arm, 1, 'S');
  }
  if (pose === 'down') {
    pix.rect(rightX, 1, arm, 5, 'c').vline(rightX + arm - 1, 1, 5, 'd').rect(rightX, 6, arm, 1, 'S');
  }
  if (pose === 'raise') {
    // o braço direito sobe: só o ombro aparece aqui; o motor desenha o antebraço ao lado da cabeça
    pix.rect(rightX, 0, arm, 2, 'c').vline(rightX + arm - 1, 0, 2, 'd');
  }
  if (pose === 'type') {
    // braços dobrados para frente: mãos em cima do teclado
    pix.rect(leftX, 1, arm, 3, 'c').rect(leftX + 1, 4, 3, 1, 'c').rect(from + 2, 5, 2, 1, back ? 'c' : 'S');
    pix.rect(rightX, 1, arm, 3, 'c').rect(rightX - 3, 4, 3, 1, 'c').rect(to - 3, 5, 2, 1, back ? 'c' : 'S');
  }
  if (!back) pix.hline(from + 2, 0, w - 4, 'd'); // gola
  return pix.outline('o').sprite(CLOTH);
}

function torsoSide(shape: BodyShape, pose: ArmPose): PixelSprite {
  const pix = new Pix(16, 8);
  const from = shape.from + 1;
  const to = shape.to - 1;
  pix.box(from, 0, to - from + 1, 7, { fill: 'c', light: 'l', dark: 'd' });
  pix.hline(from + 1, 7, to - from - 1, 'P');
  const armX = from + 2;
  if (pose === 'down') pix.rect(armX, 1, 2, 5, 'd').vline(armX, 1, 5, 'c').rect(armX, 6, 2, 1, 'S');
  if (pose === 'type') pix.rect(armX, 1, 2, 3, 'd').rect(armX - 3, 3, 4, 2, 'd').rect(armX - 4, 4, 2, 1, 'S');
  if (pose === 'raise') pix.rect(armX, 0, 2, 2, 'd');
  return pix.outline('o').sprite(CLOTH);
}

function legsFront(shape: BodyShape, pose: LegPose): PixelSprite {
  const { leg } = shape;
  const pix = new Pix(16, 6);
  const lx = 8 - leg; // perna esquerda termina na coluna 7
  const rx = 8;
  const lift = (raised: boolean) => (raised ? 1 : 0);
  if (pose === 'sit') {
    // sentado de frente: coxas curtas, canelas e sapatos
    pix.rect(lx, 0, leg * 2, 2, 'P').hline(lx, 0, leg * 2, 'Q');
    pix.rect(lx, 2, leg, 2, 'p').rect(rx, 2, leg, 2, 'p');
    pix.rect(lx, 4, leg, 1, 'K').rect(rx, 4, leg, 1, 'K');
    return pix.outline('o').sprite(CLOTH);
  }
  const upL = lift(pose === 'stepA');
  const upR = lift(pose === 'stepB');
  pix.rect(lx, 0, leg, 4 - upL, 'P').vline(lx, 0, 4 - upL, 'Q');
  pix.rect(rx, 0, leg, 4 - upR, 'P').vline(rx + leg - 1, 0, 4 - upR, 'p');
  pix.rect(lx, 4 - upL, leg, 1, 'K').rect(rx, 4 - upR, leg, 1, 'K');
  return pix.outline('o').sprite(CLOTH);
}

function legsSide(shape: BodyShape, pose: LegPose): PixelSprite {
  const pix = new Pix(16, 6);
  const x = 6;
  if (pose === 'sit') {
    pix.rect(x - 3, 0, 6, 2, 'P').hline(x - 3, 0, 6, 'Q');
    pix.rect(x - 3, 2, 2, 2, 'p').rect(x - 4, 4, 3, 1, 'K');
    return pix.outline('o').sprite(CLOTH);
  }
  if (pose === 'stand') {
    pix.rect(x, 0, shape.leg + 1, 4, 'P').vline(x, 0, 4, 'Q');
    pix.rect(x - 1, 4, shape.leg + 2, 1, 'K');
  } else {
    // passada: uma perna à frente, outra atrás
    const front = pose === 'stepA' ? -2 : 2;
    pix.rect(x + front, 1, 2, 3, 'P').rect(x + front - 1, 4, 3, 1, 'K');
    pix.rect(x - front, 0, 2, 4, 'p').rect(x - front - 1, 4, 3, 1, 'k');
    pix.rect(x - 1, 0, 4, 1, 'P');
  }
  return pix.outline('o').sprite(CLOTH);
}

/** Monta um corpo completo a partir da silhueta. */
export function makeBody(id: string, name: string, shape: BodyShape, head: DirSet<PixelSprite> = { down: HEAD_DOWN, up: HEAD_UP, left: HEAD_LEFT }): BodyDef {
  const arms = (f: (pose: ArmPose) => PixelSprite) => ({ down: f('down'), type: f('type'), raise: f('raise') });
  const legs = (f: (pose: LegPose) => PixelSprite) => ({ stand: f('stand'), stepA: f('stepA'), stepB: f('stepB'), sit: f('sit') });
  return {
    id,
    name,
    head,
    torso: {
      down: arms((p) => torsoFront(shape, p, false)),
      up: arms((p) => torsoFront(shape, p, true)),
      left: arms((p) => torsoSide(shape, p)),
    },
    legs: {
      down: legs((p) => legsFront(shape, p)),
      up: legs((p) => legsFront(shape, p)),
      left: legs((p) => legsSide(shape, p)),
    },
  };
}

export const BODIES: readonly BodyDef[] = [
  makeBody('padrao', 'Padrão', { from: 4, to: 11, arm: 2, leg: 3 }),
  makeBody('esguio', 'Esguio', { from: 5, to: 10, arm: 1, leg: 2 }),
  makeBody('robusto', 'Robusto', { from: 3, to: 12, arm: 2, leg: 4 }),
];
